import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { ClipboardList, Upload, Eye, Trash2 } from "lucide-react"
import { externalEntityService } from "../../services/externalEntityService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
} from "../common/index.js"

const ExternalMLList = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const fileInputRef = useRef(null)

  const [externalMLs, setExternalMLs] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [filters, setFilters] = useState({ search: "" })
  const [uploadFile, setUploadFile] = useState(null)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    fetchExternalMLs()
  }, [filters, currentPage])

  const fetchExternalMLs = async () => {
    try {
      setLoading(true)
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "")
      )
      const response = await externalEntityService.getAllExternalML(currentPage, 20, cleanFilters)
      const data = response.data

      // Backend returns: { success, pagination, count, ml_sheets: [...] }
      setExternalMLs(Array.isArray(data.ml_sheets) ? data.ml_sheets : [])
      setTotalPages(data.pagination?.total_pages || 1)
      setTotalRecords(data.pagination?.total || 0)
    } catch (error) {
      console.error("Error fetching External MLs:", error)
      Alert.error(error.response?.data?.message || "Failed to load External ML Sheets")
      setExternalMLs([])
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (values) => {
    setFilters(values)
    setCurrentPage(1)
  }

  const handleClearFilters = () => {
    setFilters({ search: "" })
    setCurrentPage(1)
  }

  const handleFileChange = (e) => {
    setUploadFile(e.target.files[0] || null)
  }

  const handleUpload = async () => {
    if (!uploadFile) {
      Alert.error("Please select a PDF file to upload")
      return
    }
    if (!uploadFile.name.toLowerCase().endsWith(".pdf")) {
      Alert.error("Only PDF files are accepted")
      return
    }
    try {
      setUploading(true)
      // uploadMLSheetPDF(pdfFile, userId) → POST /api/external-entities/upload-ml-sheet
      await externalEntityService.uploadMLSheetPDF(uploadFile, user?.user_id)
      Alert.success("PDF uploaded — processing in background. Refresh in a moment.")
      setUploadFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ""
      // Give the background processor a couple seconds then reload
      setTimeout(() => fetchExternalMLs(), 3000)
    } catch (error) {
      console.error("Error uploading PDF:", error)
      Alert.error(error.response?.data?.message || "Failed to upload ML Sheet PDF")
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (externalMLId) => {
    if (!window.confirm("Are you sure you want to delete this External ML Sheet?")) return
    try {
      await externalEntityService.deleteExternalML(externalMLId)
      Alert.success("External ML Sheet deleted successfully")
      fetchExternalMLs()
    } catch (error) {
      console.error("Error deleting External ML Sheet:", error)
      Alert.error(error.response?.data?.message || "Failed to delete External ML Sheet")
    }
  }

  const canEdit = () =>
    ["System Admin", "Admin", "Accountant"].includes(user?.role)

  const canDelete = () =>
    ["System Admin", "Admin"].includes(user?.role)

  const columns = [
    {
      key: "sr_no",
      label: "Sr No.",
      width: "80px",
      render: (_, index) => (currentPage - 1) * 20 + index + 1,
    },
    {
      key: "external_ml_id",
      label: "ML ID",
      render: (ml) => (
        <button
          onClick={() => navigate(`/external-ml/view/${ml.external_ml_id}`)}
          className="font-medium text-brand hover:underline"
        >
          {ml.external_ml_id}
        </button>
      ),
    },
    {
      key: "trip_sheet_no",
      label: "Trip Sheet No",
      render: (ml) => ml.trip_sheet_no || "—",
    },
    {
      key: "driver_name",
      label: "Driver",
      render: (ml) =>
        ml.driver_name
          ? `${ml.driver_name}${ml.driver_contact ? ` (${ml.driver_contact})` : ""}`
          : "—",
    },
    {
      key: "external_shipment_count",
      label: "Shipments",
      render: (ml) => ml.external_shipment_count || 0,
    },
    {
      key: "total_topay",
      label: "Total To Pay",
      render: (ml) => `₹${(ml.total_topay || 0).toFixed(2)}`,
    },
    {
      key: "created_at",
      label: "Created",
      render: (ml) =>
        ml.created_at
          ? new Date(ml.created_at).toLocaleDateString("en-IN")
          : "—",
    },
  ]

  const getActions = (ml) => [
    {
      label: "View",
      icon: Eye,
      onClick: () => navigate(`/external-ml/view/${ml.external_ml_id}`),
    },
    {
      label: "Delete",
      icon: Trash2,
      variant: "danger",
      onClick: () => handleDelete(ml.external_ml_id),
      visible: canDelete(),
    },
  ]

  return (
    <PageLayout title="External ML Sheets" icon={ClipboardList}>
      {/* Upload Section */}
      {canEdit() && (
        <div className="bg-white rounded-card p-4 sm:p-6 mb-6 border border-gray-100 shadow-card">
          <h3 className="text-sm font-semibold text-secondary mb-3">
            Upload Trip Sheet PDF
          </h3>
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
            <div className="flex-1 w-full">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="input-clean text-sm"
              />
            </div>
            <Button
              icon={Upload}
              onClick={handleUpload}
              disabled={uploading || !uploadFile}
              loading={uploading}
            >
              {uploading ? "Uploading..." : "Upload PDF"}
            </Button>
          </div>
        </div>
      )}

      <FilterPanel
        filters={[
          {
            key: "search",
            label: "Search",
            placeholder: "Search by ML ID, Trip Sheet No, Driver...",
          },
          {
            key: "from_location_id",
            label: "From Location ID",
            type: "number",
            placeholder: "From location ID",
          },
          {
            key: "to_location_id",
            label: "To Location ID",
            type: "number",
            placeholder: "To location ID",
          },
          {
            key: "vehicle_id",
            label: "Vehicle ID",
            type: "number",
            placeholder: "Vehicle ID",
          },
        ]}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
      />

      <DataTable
        columns={columns}
        data={externalMLs}
        loading={loading}
        emptyMessage="No External ML Sheets found"
        rowKey="external_ml_id"
        actions={getActions}
      />

      {!loading && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </PageLayout>
  )
}

export default ExternalMLList
